import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { DashboardService } from '@treasury/domain/channel/services/dashboard/dashboard-service';
import { Notification } from '@treasury/domain/channel/types/notifications.dto';
import { TmBaseComponent } from '@treasury/presentation';
import { notificationsIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-loader';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../components/jhd-tooltip';

export const tagName = 'notifications-header-item';
@customElement(tagName)
export class NotificationsHeaderItem extends TmBaseComponent {
    @property({ type: Boolean })
    public showNotificationsTooltip = false;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @state()
    private loading = true;

    @state()
    private notifications: Notification[] = [];

    @state()
    private totalNotifications = 0;

    @InjectProperty()
    declare private readonly client: DashboardService;

    protected async firstUpdated() {
        this.fetchNotifications();
    }

    protected render() {
        if (this.loading) {
            return html`<tm-loader reversed></tm-loader>`;
        }
        return this.showNotificationsTooltip
            ? html`<jhd-tooltip direction="bottom-left" light is-clickable
                  ><div class="icon-wrapper">
                      ${notificationsIcon}${this.renderNotificationsCount()}
                  </div>
                  <div slot="content" style="width:290px;">${this.renderNotificationsList()}</div>
              </jhd-tooltip>`
            : html`<div class="icon-wrapper">
                  ${notificationsIcon}${this.renderNotificationsCount()}
              </div>`;
    }

    private async fetchNotifications() {
        try {
            const { notifications, count } = await this.client.getRecentNotifications();
            this.notifications = notifications;
            this.totalNotifications = count;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    private handleKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.client.logNotificationActivity();
        }
    }

    private async markNotificationsAsRead(notifications: Notification[]) {
        const notificationIds = notifications.map((notification: Notification) => ({
            id: notification.id,
        }));
        try {
            await this.client.markNotificationsAsRead(notificationIds);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        } finally {
            notifications.forEach((notification: Notification) => {
                const n = { ...notification, detailsVisible: !notification.detailsVisible };
                this.renderNotificationDetail(n);
            });
        }
    }

    private async archiveNotifications(notifications: Notification[]) {
        const notificationIds = notifications
            .filter((notification: Notification) => !notification.isRead)
            .map((notification: Notification) => ({ id: notification.id }));
        try {
            await this.client.archiveNotifications(notificationIds);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        } finally {
            this.fetchNotifications();
        }
    }

    private routeToNotificationsCenter() {
        this.navService.navigate(`/notifications`);
    }

    private renderNotificationDetail(notification: Notification) {
        if (notification.detailsVisible) {
            return html`<div class="notification-detail">${notification.detail}</div>`;
        }
        return nothing;
    }

    private renderNotificationsList() {
        const notificationItems = this.notifications.map(
            (notification: Notification) =>
                html`<div
                    class="flex flex-row justify-between notification-item"
                    @click=${() => {
                        this.markNotificationsAsRead([notification]);
                    }}
                    @keyup=${(event: KeyboardEvent) => this.handleKeyUp(event)}
                >
                    <div>
                        <span class="notification-title">${notification.subject}</span><br />
                        <span class="notification-subtitle">${notification.createdOn}</span>
                    </div>
                    <omega-button
                        type="link"
                        @click=${() => {
                            this.archiveNotifications([notification]);
                        }}
                        >Archive</omega-button
                    >
                    ${this.renderNotificationDetail(notification)}
                </div>`
        );

        return html`<div class="flex flex-row notifications-header">
                <tm-icon color="var(--jh-color-gray-0)" .icon=${notificationsIcon}></tm-icon>
                <span>&nbsp;Notifications</span>
                <omega-button
                    type="link"
                    posture="small"
                    @click=${() => this.routeToNotificationsCenter()}
                    >View All</omega-button
                >
                <omega-button
                    type="link"
                    posture="small"
                    @click=${(event: Event) => {
                        event.stopPropagation();
                        this.archiveNotifications(this.notifications);
                    }}
                    >Archive All</omega-button
                >
            </div>
            <div id="notifications-list">${notificationItems}</div>`;
    }

    private renderNotificationsCount() {
        if (!this.totalNotifications) {
            return nothing;
        }
        return html`<div
            class="absolute -top-2 -end-2 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-xs text-white whitespace-nowrap bg-[--jh-color-red-500]"
        >
            ${this.totalNotifications}
        </div>`;
    }

    public static get styles() {
        return [
            css`
                path {
                    fill: var(--jh-color-gray-0);
                }
                span path {
                    fill: #8e8d8d;
                }
                small {
                    position: absolute;
                    background-color: rgb(215, 61, 9);
                    color: rgb(255, 255, 255);
                    padding: 1px;
                    margin-top: -32px;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    text-align: center;
                }
                .notifications-header {
                    font-size: 12px;
                    border-bottom: 1px solid #e2e8f0;
                    padding-bottom: 5px;
                    align-items: center;
                    margin-bottom: 5px;
                    color: var(--header-text-color);
                }
                .notification-title {
                    font-weight: 600;
                    font-size: 13px;
                    color: var(--primary-text-color);
                }
                .notification-subtitle {
                    font-weight: normal;
                    color: var(--secondary-text-color);
                }
                .icon-wrapper {
                    position: relative;
                }
                .notification-item {
                    margin-top: 5px;
                    align-items: center;
                }
                #notifications-list {
                    max-height: 400px;
                    overflow-y: auto;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: NotificationsHeaderItem;
    }
}
